import Papa from 'papaparse';
import type { Curve, ParsedData } from '../../../interfaces/toolData';

import { cleanTimeSeriesData } from '../../dataQuality';

/**
 * Parser for Pro Pulser memory log files
 * Handles files with format: "SN##### Date_#_##_#### Time_##H##M##S_HW_#_FW_#_#_SW_#_Pulser_log.csv"
 */
export class ProPulserParser {
  parse(content: string, filename: string): ParsedData {
    try {
      if (!content || content.trim() === '') {
        throw new Error('File is empty');
      }

      // Parse CSV with standard settings
      const parseResult = Papa.parse(content, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim(),
        dynamicTyping: false // Keep everything as strings initially
      });

      // Filter out field count mismatch warnings (caused by trailing commas)
      // Only show other types of parsing errors
      if (parseResult.errors.length > 0) {
        const significantErrors = parseResult.errors.filter(error => 
          error.code !== 'TooManyFields' && error.code !== 'TooFewFields'
        );
        
        if (significantErrors.length > 0) {
          console.warn('CSV parsing warnings:', significantErrors);
        } else if (parseResult.errors.length > 100) {
          // Only log if there are many field mismatches (indicates systematic issue)
          const fieldMismatches = parseResult.errors.filter(error => 
            error.code === 'TooManyFields' || error.code === 'TooFewFields'
          );
          console.warn(`CSV parsing: ${fieldMismatches.length} field count mismatches (likely trailing commas)`);
        }
      }

      const rawData = parseResult.data as Record<string, string>[];
      
      // Extract metadata from filename and data
      const metadata = this.parseMetadata(filename, rawData);
      
      // Create time series curves from the data
      const curves = this.createTimeSeriesCurves(rawData);
      
      // Create data preview
      const dataPreview = {
        totalCount: rawData.length,
        first5: rawData.slice(0, 5),
        last5: rawData.slice(-5)
      };

      return {
        toolType: 'ProPulser',
        fileType: 'PROD_PULSER_DEFAULT',
        curves,
        metadata,
        dataPreview,
        errors: parseResult.errors?.map(e => e.message) || []
      };

    } catch (error) {
      throw new Error(`Failed to parse Pro Pulser file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Parse metadata from filename and data content
   */
  private parseMetadata(filename: string, rows: Record<string, string>[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    // Extract information from filename pattern
    // Pattern: SN##### Date_#_##_#### Time_##H##M##S_HW_#_FW_#_#_SW_#_Pulser_log.csv
    const filenameMatch = filename.match(/SN(\d+)\s+Date_(\d+)_(\d+)_(\d+)\s+Time_(\d+)H(\d+)M(\d+)S_HW_(\d+)_FW_(\d+)_(\d+)_SW_(\d+)_Pulser_log\.csv/);
    
    if (filenameMatch) {
      const [, serialNumber, month, day, year, hour, minute, second, hwVersion, fwMajor, fwMinor, swVersion] = filenameMatch;
      
      metadata.serialNumber = serialNumber;
      metadata.logDate = `${month}/${day}/${year}`;
      metadata.logTime = `${hour}:${minute}:${second}`;
      metadata.hardwareVersion = hwVersion;
      metadata.firmwareVersion = `${fwMajor}.${fwMinor}`;
      metadata.softwareVersion = swVersion;
    }
    
    // Add basic statistics from the data
    if (rows.length > 0) {
      const firstRow = rows[0];
      const lastRow = rows[rows.length - 1];
      
      metadata.recordingStart = firstRow.DateTime;
      metadata.recordingEnd = lastRow.DateTime;
      metadata.totalRecords = String(rows.length);
      
      // Calculate temperature range if available
      const temperatures = rows
        .map(row => parseFloat(row.Temp_C))
        .filter(temp => !isNaN(temp));
      
      if (temperatures.length > 0) {
        const min = Math.min(...temperatures);
        const max = Math.max(...temperatures);
        const avg = temperatures.reduce((a, b) => a + b, 0) / temperatures.length;
        metadata.temperatureRange = `${min.toFixed(2)} to ${max.toFixed(2)}°C (avg: ${avg.toFixed(2)}°C)`;
      }
    }
    
    return metadata;
  }

  /**
   * Create time series curves from Pro Pulser data
   */
  private createTimeSeriesCurves(rawData: Record<string, string>[]): Curve[] {
    const curves: Curve[] = [];
    
    if (rawData.length === 0) {
      return curves;
    }

    // Parse datetime values - Pro Pulser uses DateTime column
    const timeValues = rawData.map(row => {
      const dateStr = row.DateTime;
      if (!dateStr) return null;
      
      try {
        // Handle format: "1/1/2025 12:00:00 AM"
        return new Date(dateStr);
      } catch (e) {
        return null;
      }
    }).filter(date => date !== null) as Date[];

    // If we don't have valid dates, can't create meaningful curves
    if (timeValues.length === 0) {
      return curves;
    }

    // Define the numeric columns we want to create curves for
    const numericColumns = [
      { key: 'UpTime', name: 'Up Time', unit: 'hours' },
      { key: 'Temp_C', name: 'Temperature', unit: '°C' },
      { key: 'V_5', name: '5V Rail Voltage', unit: 'V' },
      { key: 'I_5', name: '5V Rail Current', unit: 'mA' },
      { key: 'V_Main', name: 'Main Voltage', unit: 'V' },
      { key: 'I_Peak', name: 'Peak Current', unit: 'mA' },
      { key: 'I_Avg', name: 'Average Current', unit: 'mA' },
      { key: 'Trigger_Cycles', name: 'Trigger Cycles', unit: 'count' },
      { key: 'Hall_Opens', name: 'Hall Opens', unit: 'count' },
      { key: 'Hall_Closes', name: 'Hall Closes', unit: 'count' },
      { key: 'Hopen', name: 'Hall Open Events', unit: 'count' },
      { key: 'Hclose', name: 'Hall Close Events', unit: 'count' },
      { key: 'Pulse_Energy', name: 'Pulse Energy', unit: 'J' },
      { key: 'Triggers', name: 'Triggers', unit: 'count' },
      { key: 'Flow_Hrs', name: 'Flow Hours', unit: 'hours' },
      { key: 'Flow_Cycles', name: 'Flow Cycles', unit: 'count' },
      { key: 'Flow_State', name: 'Flow State', unit: 'state' },
      { key: 'Hall_Errors', name: 'Hall Errors', unit: 'count' },
      { key: 'Resets', name: 'Resets', unit: 'count' },
      { key: 'page#', name: 'Memory Page', unit: 'page' },
      { key: 'X_accel', name: 'X Acceleration', unit: 'g' },
      { key: 'Y_accel', name: 'Y Acceleration', unit: 'g' },
      { key: 'Z_accel', name: 'Z Acceleration', unit: 'g' },
      { key: 'XY_accel_ravg', name: 'XY Accel Running Avg', unit: 'g' },
      { key: 'XY_flow_threshold', name: 'XY Flow Threshold', unit: 'g' },
      { key: 'Overcurrent_cntr', name: 'Overcurrent Counter', unit: 'count' },
      { key: 'BEMF_Status', name: 'BEMF Status', unit: 'status' }
    ];

    // Create curves for each numeric column
    for (const column of numericColumns) {
      const yValues = rawData.map(row => {
        const value = parseFloat(row[column.key]);
        return isNaN(value) ? 0 : value;
      });

      // Only create curve if we have some non-zero values
      if (yValues.some(v => v !== 0)) {
        // Create initial curve object
        const initialCurve: Curve = {
          name: column.name,
          xValues: timeValues,
          yValues,
          yUnit: column.unit
        };
        
        // Apply data cleaning
        const cleanupResult = cleanTimeSeriesData(initialCurve);
        
        curves.push(cleanupResult.cleanedCurve);
      }
    }

    return curves;
  }
}

/**
 * Check if content appears to be a Pro Pulser file
 */
export function isProPulserFile(content: string): boolean {
  // Check for specific column headers that are unique to Pro Pulser
  const headers = ['DateTime', 'UpTime', 'Temp_C', 'Hall_Opens', 'Hall_Closes', 'BEMF_Status'];
  const hasAllHeaders = headers.every(header => content.includes(header));
  
  // Additional check for the specific combination of voltage and current columns
  const hasVoltageCurrentPattern = content.includes('V_5') && 
                                   content.includes('I_5') && 
                                   content.includes('V_Main') && 
                                   content.includes('I_Peak');
  
  // Accept files with basic ProPulser signature OR newer format with additional columns
  return hasAllHeaders && hasVoltageCurrentPattern;
}

/**
 * Check if filename matches Pro Pulser pattern
 */
export function isProPulserFilename(filename: string): boolean {
  // Pattern: SN##### Date_#_##_#### Time_##H##M##S_HW_#_FW_#_#_SW_#_Pulser_log.csv
  const pattern = /SN\d+\s+Date_\d+_\d+_\d+\s+Time_\d+H\d+M\d+S_HW_\d+_FW_\d+_\d+_SW_\d+_Pulser_log\.csv$/;
  return pattern.test(filename);
} 